import Heading from '@atlaskit/heading';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog';
import Modal, { ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { JiraLogo } from '@atlaskit/logo';
import { cssMap } from '@atlaskit/css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PlaceOrderLite } from '@atlassian/place-order-lite/place-order-lite';
import { ANALYTICS_CHANNEL, useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import Lottie from 'lottie-react';
import { type JSX } from 'react/jsx-runtime';
import { type RecommendedFeature, type RecommendedEdition } from './types';
import { MODAL_CONTENT } from './modal-content';
import { useIntl } from 'react-intl';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';

export interface UpgradeModalProps {
	isOpen: boolean;
	/** Called when the user closes the modal without completing the trial flow. */
	onClose: () => void;
	/** Called when the trial is successfully started — closes modal AND dismisses the banner. */
	onComplete: () => void;
	recommendedFeature: RecommendedFeature;
	edition: RecommendedEdition;
	trialLength: number;
	headlineCount: number;
	placeOrderLiteHref: string;
}

const modalStyles = cssMap({
	root: {
		gridTemplateColumns: '1fr 484px',
		height: '100%',
	},
	darkSurface: {
		backgroundColor: token('elevation.surface'),
	},
	feature: {
		gap: token('space.150'),
		alignItems: 'center',
	},
	left: {
		padding: token('space.600'),
		gap: token('space.600'),
		justifyContent: 'space-between',
		display: 'flex',
	},
	right: {
		position: 'relative',
		overflow: 'hidden',
		borderTopRightRadius: token('radius.large'),
		borderBottomRightRadius: token('radius.large'),
		width: '484px',
		height: '560px',
	},
	rightHeader: {
		position: 'absolute',
		top: 0,
		right: 0,
		zIndex: 1,
	},
	animation: {
		width: '100%',
		height: '100%',
		margin: 0,
		padding: 0,
	},
});

/**
 * Webpack cannot reliably analyze dynamic paths (template literals) for chunking.
 * Using explicit static import paths ensures Webpack correctly bundles each animation
 * JSON as a separate async chunk.
 */
const ModalSideAnimation = ({
	isDarkMode,
	recommendedFeature,
}: {
	isDarkMode: boolean;
	recommendedFeature: RecommendedFeature;
}): JSX.Element => {
	const [animationData, setAnimationData] = useState<Record<string, unknown> | null>(null);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	useEffect(() => {
		const loadJson = async () => {
			try {
				// Each import path must be a static string literal for Webpack chunking.
				// recommendedFeature and isDarkMode are used to select the correct branch.
				const data = await (recommendedFeature === 'plans'
					? isDarkMode
						? import(
								/* webpackChunkName: "async-animation-plans-dark" */
								'./assets/Plans-Dark.json'
							)
						: import(
								/* webpackChunkName: "async-animation-plans-light" */
								'./assets/Plans-Light.json'
							)
					: recommendedFeature === 'automations'
						? isDarkMode
							? import(
									/* webpackChunkName: "async-animation-automations-dark" */
									'./assets/Automations-Dark.json'
								)
							: import(
									/* webpackChunkName: "async-animation-automations-light" */
									'./assets/Automations-Light.json'
								)
						: isDarkMode
							? import(
									/* webpackChunkName: "async-animation-rovo-dark" */
									'./assets/Rovo-Dark.json'
								)
							: import(
									/* webpackChunkName: "async-animation-rovo-light" */
									'./assets/Rovo-Light.json'
								));

				const json = data.default || data;
				/**
				 * Lottie mutates the animationData object internally during playback.
				 * We use JSON.parse(JSON.stringify()) to provide a clean deep copy,
				 * preventing side effects when the same JSON is reused across re-renders or theme changes.
				 */
				// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
				setAnimationData(JSON.parse(JSON.stringify(json)));
			} catch (error) {
				fireAnalyticsEvent({
					eventType: 'operational',
					action: 'failed',
					actionSubject: 'lottieAnimationLoad',
					actionSubjectId: 'jiraTwcg810UpgradeModal',
					attributes: {
						team: 'growth-luna',
						recommendedFeature,
					},
				});
			}
		};

		void loadJson();
	}, [isDarkMode, fireAnalyticsEvent, recommendedFeature]);

	if (!animationData) {
		return <Box xcss={modalStyles.animation} />;
	}

	return (
		<Box xcss={modalStyles.animation}>
			<Lottie animationData={animationData} loop autoplay />
		</Box>
	);
};

export const UpgradeModal = ({
	isOpen,
	onClose,
	onComplete,
	recommendedFeature,
	edition,
	trialLength,
	headlineCount,
	placeOrderLiteHref,
}: UpgradeModalProps): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	const placeOrderHrefWithTheme = `${placeOrderLiteHref}&theme=${isDarkMode ? 'dark' : 'light'}`;

	const hasFiredViewedRef = useRef(false);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	const intl = useIntl();

	const handleError = useCallback(
		(error?: Error) => {
			fireAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'trialAction',
				actionSubjectId: 'jiraTwcg810UpgradeModal',
				attributes: {
					team: 'growth-luna',
					errorMessage: error?.message,
				},
			});
		},
		[fireAnalyticsEvent],
	);

	const handleOnOrderPlaced = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'tryNow',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {
				completionStatus: 'success',
				wasEffectivelyOnClick: true,
			},
		});
	}, [messageInstanceClicked]);

	useEffect(() => {
		if (!isOpen) return;
		if (hasFiredViewedRef.current) return;

		hasFiredViewedRef.current = true;
		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'jiraTwcg810UpgradeModal',
			attributes: {
				team: 'growth-luna',
				recommendedFeature,
				edition,
				trialLength,
			},
		});
	}, [isOpen, messageInstanceViewed, fireAnalyticsEvent, recommendedFeature, edition, trialLength]);

	const { HeadingMessage, BodyMessage, features } = MODAL_CONTENT[recommendedFeature];

	const handleOnClose = useCallback(
		(_e: KeyboardOrMouseEvent, analyticEvent: UIAnalyticsEvent) => {
			// Reuse the event provided by @atlaskit/modal-dialog so we keep its
			// context (e.g. the Modal/component path) while still setting the
			// Post Office actionSubjectId we rely on for reporting.
			analyticEvent
				.update({
					eventType: 'ui',
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'jiraTwcg810UpgradeModalClose',
				})
				.fire(ANALYTICS_CHANNEL);
			onClose();
		},
		[onClose],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal width={968} height={560} onClose={handleOnClose}>
					<Box xcss={isDarkMode ? modalStyles.darkSurface : undefined}>
						<Grid xcss={modalStyles.root}>
							<Stack xcss={modalStyles.left}>
								<Stack space="space.400">
									<JiraLogo size="small" appearance="brand" shouldUseNewLogoDesign />
									<Stack space="space.200">
										<Heading size="large" as="h1">
											<HeadingMessage count={headlineCount} />
										</Heading>
										<Stack space="space.300">
											<Text>
												<BodyMessage trialLength={trialLength} edition={edition} />
											</Text>
											<Stack space="space.200">
												{features.map(({ id, Icon, message }) => (
													<Flex key={id} xcss={modalStyles.feature}>
														<Icon size="medium" label="" color={token('color.icon.brand')} />
														<Text>{message}</Text>
													</Flex>
												))}
											</Stack>
										</Stack>
									</Stack>
								</Stack>

								<PlaceOrderLite
									href={placeOrderHrefWithTheme}
									product="jira"
									edition={edition}
									onComplete={onComplete}
									onOrderPlaced={handleOnOrderPlaced}
									onError={handleError}
									primaryAction={intl.formatMessage({
										id: 'growth-luna.jira-twcg-810-upgrade-free.action.primary.pde',
										defaultMessage: 'Try it free',
										description: 'Start trial with payment details on file',
									})}
									actionsAlignment="horizontal"
								/>
							</Stack>
							<Box xcss={modalStyles.right}>
								<ModalSideAnimation
									isDarkMode={isDarkMode}
									recommendedFeature={recommendedFeature}
								/>
								<Box xcss={modalStyles.rightHeader}>
									<ModalHeader hasCloseButton />
								</Box>
							</Box>
						</Grid>
					</Box>
				</Modal>
			)}
		</ModalTransition>
	);
};
